import axios from 'axios';
import { getToken, createApiUrl } from '../../../../util';
import { CurrentMovablePanel, DeletedPanel, NewlyAddedPanel, NewlyCreatedFacetHullCoords } from 'store/slices/ToolSlice/types';
import { LinesData } from 'store/slices/RoofToolLinesSlice/types';
import api from 'api/axiosInstance';
import { FacetEditShadingStatus, normalizeFacetShadingStatus } from 'api/editDsm';

async function createNewFacets(newFacets: { newPanelFacets: NewlyCreatedFacetHullCoords[] , deletedRoofSegs: string[]},
	panelKey: string, uuid: string, designType:DesignType = 'SOLAR' ): Promise<FacetEditShadingStatus> {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const facetKey = designType === 'ROOF' ? 'newRoofSegments' : 'panelFacets';

	const body = {
		[facetKey]: newFacets.newPanelFacets,
		deletedRoofSegs: newFacets.deletedRoofSegs,
		uuid,
		panelKey,
	};

	const apiUrl = `${designType === 'ROOF' ? '/designs/roof' : '/designs'}/edit-roof-segments`;
	const { data } = await api.post(createApiUrl(apiUrl), body, config);
	return data;
}

async function removeShading(uuid: string ,payload:{panelKey:string; facetId: string; isUserMapped: boolean}) {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};
	const apiUrl = `/designs/remove-roof-segment-shading/${uuid}?panelKey=${payload.panelKey}&facetId=${payload.facetId}&isUserMapped=${payload.isUserMapped}`;
	return await api.post(createApiUrl(apiUrl), null, config);
}

async function undoRemoveShading(uuid: string ,payload:{panelKey:string; facetId: string; isUserMapped: boolean}) {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};
	const apiUrl = `/designs/undo-remove-roof-segment-shading/${uuid}?panelKey=${payload.panelKey}&facetId=${payload.facetId}&isUserMapped=${payload.isUserMapped}`;
	return await api.post(createApiUrl(apiUrl), null, config);
}

async function adjustShadingInit(uuid: string ,payload:{panelKey:string; facetId: string; isUserMapped: boolean}) {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};
	const apiUrl = `/designs/adjust-shading-init-data/${uuid}?panelKey=${payload.panelKey}&facetId=${payload.facetId}&isUserMapped=${payload.isUserMapped}`;
	return await api.patch(createApiUrl(apiUrl), null, config);
}

async function adjustShading(uuid: string ,payload:{panelKey:string; facetId: string; isUserMapped: boolean, shading:number}) {
	const { facetId, isUserMapped, panelKey, shading } = payload;
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};
	const body = {
		facetId,
		isUserMapped,
		panelKey,
		shading,
	};
	const apiUrl = `/designs/adjust-shading/${uuid}`;
	return await api.patch(createApiUrl(apiUrl), body, config);
}

async function updateEditableFacets(
	editableFacets: Partial<editableFacet>[], 
	uuid: string, 
	panelKey: string, 
	designType: DesignType
): Promise<FacetEditShadingStatus> {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const body = {
		editedFacets: editableFacets,
		uuid,
		panelKey,
	};

	const apiUrl = `${designType === 'ROOF' ? '/designs/roof' : '/designs'}/update-facets-coords`;
	const { data } = await api.post(createApiUrl(apiUrl), body, config);
	return data;
}

/**
 * The three panel-side edits (add / delete / move) are facet edits too: a panel on a flat
 * ground mount is a rack baked into the DSM, so the API bumps the DSM version and answers
 * with the same FacetEditShadingStatus the facet endpoints return. Undefined means the API
 * decided nothing is owed (no flat ground mount touched, or not an in-house design) — the
 * FE never has to work that out itself.
 */
async function addNewPanels(
	panels: NewlyAddedPanel[], panelKey: string, uuid: string
): Promise<FacetEditShadingStatus | undefined> {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const body = {
		panels,
		uuid,
		panelKey
	};

	const apiUrl = '/designs/add-panels-exterior-coords';
	const { data } = await api.post(createApiUrl(apiUrl), body, config);
	return normalizeFacetShadingStatus(data);
}

async function removeIndividualPanels(
	panels: DeletedPanel[], panelKey: string, uuid: string
): Promise<FacetEditShadingStatus | undefined> {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const body = {
		panels,
		uuid,
		panelKey
	};

	const apiUrl = '/designs/remove-panels';
	const { data } = await api.patch(createApiUrl(apiUrl), body, config);
	return normalizeFacetShadingStatus(data);
}

async function movablePanels(
	panels: Partial<CurrentMovablePanel>[], panelKey: string, uuid: string
): Promise<FacetEditShadingStatus | undefined> {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const body = {
		panels,
		uuid,
		panelKey
	};

	const apiUrl = '/designs/transform-panels';
	const { data } = await api.patch(createApiUrl(apiUrl), body, config);
	return normalizeFacetShadingStatus(data);
}

async function sendDebugData(jsonData: any){
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};

	const body = {
		jsonData,
	};

	const apiUrl = '/notification/send-debug-data';
	const { data } = await api.post(createApiUrl(apiUrl), body, config);
	return data;
}

async function updateRoofToolFacet(upsertData: Partial<LinesData & {
	autoDetectedEdgesEnabled?: boolean;
}>, uuid: string, designType: 'ROOF'|'SOLAR') {
	const token = getToken('Authorization');
	const config = {
		headers: {
			'Authorization': `Bearer ${token}`
		}
	};
	const body = {
		uuid,
		...upsertData
	};
	const apiUrl = designType === 'ROOF' ? '/designs/roof/roof-segment-edges/upsert' : '/designs/upsert-special-edges';
	const { data } = await api.post(createApiUrl(apiUrl), body, config);
	return data;
}

export {
	createNewFacets,
	removeShading,
	undoRemoveShading,
	updateEditableFacets,
	addNewPanels,
	removeIndividualPanels,
	movablePanels,
	sendDebugData,
	updateRoofToolFacet,
	adjustShadingInit,
	adjustShading,
};